import { Group, Menu, Text } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { SignatureRow, OnStatusChange, OnNoLongerAccessible } from 'components/tables/data/signature/types';
import { useApi } from 'api/api-context';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { useMemo } from 'react';
import SignAction from 'components/tables/data/signature/actions/SignAction';
import ViewAction from 'components/tables/data/signature/actions/ViewAction';
import SignAgainAction from 'components/tables/data/signature/actions/SignAgainAction';
import CancelSignAction from 'components/tables/data/signature/actions/CancelSignAction';
import ActionIcon from 'components/ActionIcon';

/**
 * Enum of all possible action names.
 */
type ActionName = 'sign' | 'view' | 'sign-again' | 'cancel-sign';

/**
 * Discriminates between different actions.
 */
export function ActionDiscriminator({
  actionName,
  ...params
}: {
  actionName: ActionName;
  signature: SignatureRow;
  kind: 'button' | 'menu' | 'action-icon';
  onStatusChange?: OnStatusChange;
  onNoLongerAccessible?: OnNoLongerAccessible;
}) {
  const actionNameMap = useMemo(
    () => ({
      sign: SignAction,
      view: ViewAction,
      'sign-again': SignAgainAction,
      'cancel-sign': CancelSignAction,
    }),
    []
  );

  const Component = actionNameMap[actionName];

  if (Component) {
    return <Component {...params} />;
  }

  return <></>;
}

/**
 * Parameters of the SignatureActions component.
 */
export interface SignatureActionsProps {
  signature: SignatureRow;
  compactOnTablet?: boolean;
  onStatusChange?: OnStatusChange;
  onNoLongerAccessible?: OnNoLongerAccessible;
}

/**
 * Displays actions for a signature in a table.
 */
export default function SignatureActions({
  signature,
  compactOnTablet = false,
  onStatusChange = noop,
  onNoLongerAccessible = noop,
}: SignatureActionsProps) {
  const { userId } = useApi();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';

  const actions = useMemo(() => {
    const { slug } = signature.revisionStatus;
    const { userId: technicianId = null } = signature.technician || {};
    const canSign = slug === 'being-signed' && userId === technicianId;
    return canSign ? (['sign', 'view', 'sign-again'] as const) : (['view'] as const);
  }, [signature, userId]);

  const primaryAction = useMemo(() => (menuOnly ? null : actions[0] || null), [actions, menuOnly]);
  const menuActions = useMemo(() => (menuOnly ? actions : actions.slice(1)), [actions, menuOnly]);

  if (!signature.kepMessageUuid) {
    return null;
  }

  return (
    <Group spacing={8}>
      {primaryAction && (
        <ActionDiscriminator
          actionName={primaryAction}
          signature={signature}
          kind={isCompact ? 'action-icon' : 'button'}
          onStatusChange={onStatusChange}
          onNoLongerAccessible={onNoLongerAccessible}
        />
      )}
      {menuActions.length > 0 && (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
              <IconDotsVertical stroke="1.5" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                KEP Podpis #{signature.kepMessageUuid.slice(0, 8)}
              </Text>
            </Menu.Label>
            {menuActions.map((action) => (
              <ActionDiscriminator
                key={action}
                actionName={action}
                signature={signature}
                kind="menu"
                onStatusChange={onStatusChange}
                onNoLongerAccessible={onNoLongerAccessible}
              />
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}
